import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CreateNote from './components/create-note';
import ViewNote from './components/view-note';
import ShareNote from './components/share-note';
import CookieConsent from './components/CookieConsent';
import LanguageSwitcher from './components/LanguageSwitcher';
import Footer from './components/Footer';
import Privacy from './pages/Privacy';
import Support from './pages/Support';
import FAQ from './pages/FAQ';
import { withRateLimitCheck } from './hoc/withRateLimitCheck';
import ReCaptchaProvider from './components/ReCaptchaProvider';
import SecurityCheck from './components/SecurityCheck';
import { useGlobalReCaptcha } from './hooks/useGlobalReCaptcha';
import { ReCaptchaProvider as ReCaptchaContextProvider } from './contexts/ReCaptchaContext';
import BlockedPage from './components/BlockedPage';
import { supabase } from './lib/supabase';

// Apply rate limit check to both ViewNote and AppContent
const ProtectedViewNote = withRateLimitCheck(ViewNote);
const ProtectedAppContent = withRateLimitCheck(AppContent);

function AppContent() {
  const { t } = useTranslation();
  const [showCookieConsent, setShowCookieConsent] = React.useState(false);
  const { isVerified, isLoading, error } = useGlobalReCaptcha();
  const [isGloballyBlocked, setIsGloballyBlocked] = useState(false);
  const [globalBlockedUntil, setGlobalBlockedUntil] = useState<string | null>(null);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowCookieConsent(true);
    }
  }, []);

  useEffect(() => {
    const checkGlobalBlock = async () => {
      try {
        console.log('Checking global block status...');
        const { data, error } = await supabase.rpc('get_client_ip');
        
        if (error) {
          console.error('Failed to check global block status:', error);
          return;
        }

        if (!data) {
          console.error('No data returned from get_client_ip');
          return;
        }

        console.log('Global block check response:', {
          is_globally_blocked: data.is_globally_blocked,
          global_blocked_until: data.global_blocked_until,
          blocked_notes_count: data.blocked_notes_count
        });
        
        if (data.is_globally_blocked && data.global_blocked_until) {
          const blockedUntil = new Date(data.global_blocked_until);
          const now = new Date();
          
          console.log('Block status:', {
            blockedUntil: blockedUntil.toISOString(),
            now: now.toISOString(),
            isStillBlocked: blockedUntil > now
          });

          if (blockedUntil > now) {
            console.log('User is globally blocked until:', data.global_blocked_until);
            setIsGloballyBlocked(true);
            setGlobalBlockedUntil(data.global_blocked_until);
          } else {
            console.log('Global block has expired');
            setIsGloballyBlocked(false);
            setGlobalBlockedUntil(null);
          }
        } else {
          console.log('User is not globally blocked');
          setIsGloballyBlocked(false);
          setGlobalBlockedUntil(null);
        }
      } catch (error) {
        console.error('Failed to check global block status:', {
          error,
          stack: error instanceof Error ? error.stack : undefined
        });
      }
    };

    // Check immediately and then every minute
    checkGlobalBlock();
    const interval = setInterval(checkGlobalBlock, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    console.log('AppContent state:', {
      isVerified,
      isLoading,
      error,
      isGloballyBlocked,
      globalBlockedUntil
    });
  }, [isVerified, isLoading, error, isGloballyBlocked, globalBlockedUntil]);

  if (isGloballyBlocked && globalBlockedUntil) {
    return <BlockedPage blockedUntil={globalBlockedUntil} isGlobalBlock={true} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SecurityCheck isLoading={isLoading} error={error} />
      
      {isVerified && (
        <>
          <nav className="bg-white shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16">
                <div className="flex">
                  <Link
                    to="/"
                    className="flex items-center px-2 py-2 text-gray-900 hover:text-blue-600"
                  >
                    <img src="/logo.svg" alt="Bitfortex" className="w-8 h-8 mr-2" />
                    <span className="font-semibold text-xl">Bitfortex</span>
                  </Link>
                </div>
                <div className="flex items-center">
                  <LanguageSwitcher />
                </div>
              </div>
            </div>
          </nav>

          <main className="flex-1 py-10">
            <Routes>
              <Route path="/" element={<CreateNote />} />
              <Route path="/note/:shortId" element={<ProtectedViewNote />} />
              <Route path="/share/:shortId" element={<ShareNote />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/support" element={<Support />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          <Footer />

          {showCookieConsent && (
            <CookieConsent onClose={() => setShowCookieConsent(false)} />
          )}
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ReCaptchaContextProvider>
        <ReCaptchaProvider>
          <ProtectedAppContent />
        </ReCaptchaProvider>
      </ReCaptchaContextProvider>
    </BrowserRouter>
  );
}

export default App;